import {Spinner, SpinnerSize, SpinnerStyle} from '@mintdata-wsp/common/components/spinner/Spinner';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {FlowSimulation} from '@mintdata-wsp/common/state/ui/visualDesigner/FlowState';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellValueFormatKind} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormatKind';
import {
    MeshCellBorder,
    MeshCellBorderType
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellBorder';
import {
    MeshCellTextAlign,
    MeshCellTextWrapping
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellSettings/MeshCellTextAlign';
import {MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {
    DefaultMeshCellSettings,
    MeshCellSettings
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshViewTargetType} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshViewTargetType';
import {AnySheet, AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {CellSettingsBarMenu} from '@mintdata-wsp/common/state/ui/visualDesigner/workbench/CellSettingsBarState';
import {AError} from '@mintdata-wsp/mint-kit/api/values/AError';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {ImgIcon} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcon';
import {ImgIcons} from '@mintdata/mint-kit/impl/properties/components/imgIcon/ImgIcons';
import bind from 'bind-decorator';
import * as classnames from 'classnames/bind';
import {computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import * as React from 'react';

const styles = require('@mintdata-wsp/wsp/ui/views/visualDesigner/spreadsheet/mesh/Mesh.less');
const cx = classnames.bind(styles);

enum CellValueAlign
{
    Left = 'Left',
    Center = 'Center',
    Right = 'Right'
}

interface IMeshColViewProps
{
    ctx?:IWSPMeshContext;
    colIndex:number;
    rowIndex:number;
    isNested:boolean;
}

@inject('ctx')
@observer
export class MeshCellView extends React.Component<IMeshColViewProps, any>
{
    public render():JSX.Element
    {
        const {ctx, isNested} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {size: {numCols, numRows}} = meshState;

        const {colIndex, rowIndex} = this.props;

        if( colIndex >= numCols || rowIndex >= numRows )
        {
            return null;
        }

        let vertAlign;
        const cellNote = this._note;

        const cellSettings = this._settings;

        if( cellSettings )
        {
            vertAlign = cellSettings.vertAlign;
        }

        const vertAlignClass = vertAlign ? 'vertAlign' + vertAlign : '';

        return (
            <div
                data-type={MeshViewTargetType.Cell}
                className={cx('tableCell',
                    {
                        // isInvalid: this._isInvalid,
                        isActive: this._isActive,
                        isErrorCell: this._isErrorCell,
                        isFrameCell: this._isFrameCell,
                        isLazyCell: this._isLazyCell,
                        isLiveCell: this._isLiveCell,
                        isLiveCellRunning: this._isLiveCell && this._isLiveCellRunning,
                        isMouseOver: this._isMouseOver,
                        isEnteredFrameCell: this._isEnteredFrameCell
                    })}
                style={this._style}
            >

                {this._renderBgColor()}

                {this._renderBorder()}

                <div className={cx('tableCellContent')}>
                    <div className={cx('tableCellContentInner', vertAlignClass)}>
                        {!isNested && this._renderCellActionsLeft()}
                        {/*{isNested ? this._renderedNestedFrameValue() : this._renderedValue()}*/}
                        {this._renderedValue()}
                        {!isNested && this._renderCellActionsRight()}
                    </div>
                </div>
                {
                    this._isLoading &&
                    <div className={styles.tableCellLoader}>
                        {
                            this._isCloudSheet
                                ? <div className={styles.loaderCellOverlay} />
                                : <Spinner size={SpinnerSize.Medium_24} style={SpinnerStyle.Blue} />
                        }
                    </div>
                }
                {
                    cellNote &&
                    <div
                        className={cx('note')}
                        onClick={this._onClickNote}
                    />
                }
            </div>
        );
    }

    @bind
    private _onClickNote(event:React.MouseEvent<HTMLElement>)
    {
        controllers.ui.spreadsheets.mesh.startEditingOrCreateNote(this.props.ctx);
    }

    private _renderedValue():string | JSX.Element
    {
        const {isNested} = this.props;
        const {ctx: {sheetCtrl}} = this.props;
        const slot:AnySlot = this._slot;

        if( !slot )
        {
            return null;
        }

        let value:JSX.Element | string;
        let cellSettings = this._settings;
        const cellFormat = this._format;

        let isValueMasked;
        let isTextBold;
        let isTextItalic;
        let isTextUnderline;
        let isTextStrikethrough;

        let textAlign;
        let isWrapText;
        let isClipped;

        let valueFormatter:IMeshCellValueFormat;
        if( cellSettings )
        {
            textAlign = cellSettings.textAlign;
            isValueMasked = cellSettings.isValueMasked;
        }

        if( cellFormat )
        {
            if( isValueMasked )
                valueFormatter = MeshCellValueFormat.MaskedText;
            else if( this._isErrorCell )
                valueFormatter = MeshCellValueFormat.General;
            else
                valueFormatter = cellFormat;
        }
        else
        {
            valueFormatter = MeshCellValueFormat.General;
        }

        let textStyle:React.CSSProperties = {};
        let textAlignClass = 'textAlign' + (textAlign ? textAlign : MeshCellTextAlign.Center);
        const isTextAlignCenter = textAlign && textAlign == MeshCellTextAlign.Center;

        const isLazy = this._slot && this._isLazyCell;
        const isLazyAndAlignCenter = isTextAlignCenter && isLazy;

        const isInvalid = this._isErrorCell;
        const isImage = cellFormat && cellFormat.kind == MeshCellValueFormatKind.Image;

        if( !slot.value )
        {
            if( slot.status )
            {
                return (
                    <div className={cx('tableCellSlotStatus', textAlignClass)}>
                        {slot.status.toString()}
                        {
                            isLazyAndAlignCenter &&
                            <div className={cx('rightFakeBox')} />
                        }
                    </div>
                );
            }
            const status = sheetCtrl.getSlotStatus(slot.sheet.id, slot.location);

            return status && status.toString();
        }

        if( this._isErrorCell )
        {
            const error = slot.value as AError;

            if( !textAlign ) textAlignClass = 'textAlignLeft';

            value = (
                <span
                    className={cx('errorCellContent', textAlignClass)}
                    onMouseDownCapture={this._handleFrameCellMouseDown}
                >
                    <span className={cx('rightSideError')}>
                        {error.errorContext ? 'ERROR': error.error}
                    </span>
                </span>
            )
        }
        else if( this._isFrameCell )
        {
            const frame = slot.value as IFrame;

            if( !textAlign ) textAlignClass = 'textAlignLeft';

            value = (
                <span
                    className={cx('frameCellContent', {isTextAlignCenter})}
                    onMouseDownCapture={this._handleFrameCellMouseDown}
                >
                    <span className={cx('leftSide')}>
                        <span className={cx('frameCellRowCount')}>{frame.recordCount}</span>
                    </span>
                    <span className={cx('rightSide')}>
                        {frame.recordCount === 1 ? 'row' : 'rows'}
                    </span>
                </span>
            );
        }
        else if( valueFormatter.kind == MeshCellValueFormatKind.Accounting )
        {
            let fontName;
            let fontSize;
            let textColor;
            const stringValue = valueFormatter.formatValue(this._slot.value);
            if( cellSettings )
            {
                textColor = cellSettings.textColor;
                fontName = cellSettings.fontName;
                fontSize = cellSettings.fontSize;

                // if( !Object.values(MeshCellFontName).includes(fontName) ) fontName = DefaultMeshCellSettings.fontName;

                isTextBold = cellSettings.textBold;
                isTextItalic = cellSettings.textItalic;
                isTextUnderline = cellSettings.textUnderline;
                isTextStrikethrough = cellSettings.textStrikethrough;
                isWrapText = cellSettings.wrapText === MeshCellTextWrapping.Wrap;
                isClipped = cellSettings.wrapText === MeshCellTextWrapping.Clip;

                if( textColor ) textStyle.color = textColor;
                if( fontName ) textStyle.fontFamily = fontName;
                if( fontSize ) textStyle.fontSize = `${fontSize}pt`;
            }
            value = (
                <span
                    className={cx('accountingCellContent', {isTextAlignCenter},
                        {
                            isValueMasked, isTextBold, isTextItalic, isTextUnderline,
                            isTextStrikethrough: isTextStrikethrough && !isTextUnderline,
                            isWrapText, isClipped, isLazyAndAlignCenter, isInvalid,
                            isNested, isLazy
                        })}>
                    <span className={cx('leftSide')}>
                        $
                    </span>
                    <span className={cx('rightSide', {
                        isValueMasked, isTextBold, isTextItalic, isTextUnderline,
                        isTextStrikethrough: isTextStrikethrough && !isTextUnderline,
                        isWrapText, isClipped, isLazyAndAlignCenter, isInvalid,
                        isNested, isLazy
                    })}>
                        {stringValue.substring(1, stringValue.length)}
                    </span>
                </span>
            );

        }
        else
        {
            const slotValue = this._slot.value;
            value = valueFormatter.formatValue(slotValue);

            if( !textAlign || 'General' === textAlign )
            {
                if( Type.isInstanceOf(slotValue, Type.Number) || Type.isInstanceOf(slotValue, Type.Integer) ||
                    Type.isInstanceOf(slotValue, Type.Byte) || Type.isInstanceOf(slotValue, Type.Date)
                    || Type.isInstanceOf(slotValue, Type.DateTime) )
                {
                    textAlignClass = 'textAlignRight';
                }
                else if( Type.isInstanceOf(slotValue, Type.String) || Type.isInstanceOf(slotValue, Type.BinaryData) )
                {
                    textAlignClass = 'textAlignLeft';
                }
                else if( Type.isInstanceOf(slotValue, Type.Boolean) )
                {
                    textAlignClass = 'textAlignCenter';
                }
                else if( Type.isInstanceOf(slotValue, Type.Frame) || isLazy )
                {
                    textAlignClass = 'textAlignLeft';
                }
                else
                {
                    textAlignClass = 'textAlignCenter';
                }
            }

            if( !this._isErrorCell && !isImage && cellSettings )
            {
                let fontName;
                let fontSize;
                let textColor;

                const {
                    isMenuOpened,
                    tmpValue
                } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

                if( isMenuOpened == CellSettingsBarMenu.FontName && this._cellInSelectedRange && tmpValue !== null )
                    fontName = tmpValue;
                else if( cellSettings )
                    fontName = cellSettings.fontName;

                if( isMenuOpened == CellSettingsBarMenu.FontSize && this._cellInSelectedRange && tmpValue !== null )
                    fontSize = tmpValue < DefaultMeshCellSettings.fontSizeMin
                        ? DefaultMeshCellSettings.fontSizeMin
                        : tmpValue > DefaultMeshCellSettings.fontSizeMax
                            ? DefaultMeshCellSettings.fontSizeMax
                            : tmpValue;
                else if( cellSettings )
                    fontSize = cellSettings.fontSize;

                if( isMenuOpened == CellSettingsBarMenu.TextColor && this._cellInSelectedRange && tmpValue !== null )
                    textColor = tmpValue;
                else if( cellSettings )
                    textColor = cellSettings.textColor;

                // if( !Object.values(MeshCellFontName).includes(fontName) ) fontName = DefaultMeshCellSettings.fontName;

                isTextBold = cellSettings.textBold;
                isTextItalic = cellSettings.textItalic;
                isTextUnderline = cellSettings.textUnderline;
                isTextStrikethrough = cellSettings.textStrikethrough;
                isWrapText = cellSettings.wrapText === MeshCellTextWrapping.Wrap;
                isClipped = cellSettings.wrapText === MeshCellTextWrapping.Clip;

                if( textColor ) textStyle.color = textColor;
                if( fontName ) textStyle.fontFamily = fontName;
                if( fontSize ) textStyle.fontSize = `${fontSize}pt`;
            }
            if( valueFormatter )
            {
                textStyle.color = valueFormatter.getTextColor(slotValue, cellSettings);
            }
        }

        if( isImage && !this._isFrameCell && !this._isErrorCell ) return (
            <div className={cx('tableCellValue')}>
                <div className={cx('imageWrapper', {isLazy}, textAlignClass)}>
                    <img
                        className={cx('image')}
                        src={value.toString()}
                        alt=""
                    />
                </div>
            </div>
        );

        return (
            <div
                className={cx('tableCellValue', textAlignClass,
                    {
                        isValueMasked, isTextBold, isTextItalic, isTextUnderline,
                        isTextStrikethrough: isTextStrikethrough && !isTextUnderline,
                        isWrapText, isClipped, isLazyAndAlignCenter, isInvalid,
                        isNested, isLazy
                    }
                )}
                style={textStyle}
            >
                {isTextStrikethrough && isTextUnderline && (
                    <span className={cx({isTextStrikethrough})}>{value}</span>
                )}
                {!(isTextStrikethrough && isTextUnderline) && value}

                {
                    isLazyAndAlignCenter &&
                    <div className={cx('rightFakeBox')} />
                }

            </div>
        );
    }

    // private _renderedNestedFrameValue():string | JSX.Element
    // {
    //     const {isNested} = this.props;
    //     const {ctx: {sheetCtrl}} = this.props;
    //     const slot:AnySlot = this._slot;
    //
    //     if( !slot )
    //     {
    //         return null;
    //     }
    //
    //     let value:JSX.Element | string;
    //     let valueFormatter:IMeshCellValueFormat = MeshCellValueFormat.General;
    //
    //     const isInvalid = this._isInvalid;
    //
    //     let textAlignClass = 'textAlign' + MeshCellTextAlign.Center;
    //
    //     if( this._isFrameCell )
    //     {
    //         const frame = slot.value as IFrame;
    //
    //         textAlignClass = 'textAlignLeft';
    //
    //         value = (
    //             <span
    //                 className={cx('frameCellContent')}
    //                 onMouseDownCapture={this._handleFrameCellMouseDown}
    //             >
    //                 <span className={cx('leftSide')}>
    //                     <span className={cx('frameCellRowCount')}>{frame.recordCount}</span>
    //                 </span>
    //                 <span className={cx('rightSide')}>
    //                     {frame.recordCount === 1 ? 'row' : 'rows'}
    //                 </span>
    //             </span>
    //         );
    //     }
    //     else
    //     {
    //         const slotValue = this._slot.value;
    //         value = valueFormatter.formatValue(slotValue);
    //
    //         if( Type.isInstanceOf(slotValue, Type.Number) || Type.isInstanceOf(slotValue, Type.Integer) ||
    //             Type.isInstanceOf(slotValue, Type.Byte) || Type.isInstanceOf(slotValue, Type.Date)
    //             || Type.isInstanceOf(slotValue, Type.DateTime) )
    //         {
    //             textAlignClass = 'textAlignRight';
    //         }
    //         else if( Type.isInstanceOf(slotValue, Type.String) || Type.isInstanceOf(slotValue, Type.BinaryData) )
    //         {
    //             textAlignClass = 'textAlignLeft';
    //         }
    //         else if( Type.isInstanceOf(slotValue, Type.Boolean) )
    //         {
    //             textAlignClass = 'textAlignCenter';
    //         }
    //             // else if( Type.isInstanceOf(slotValue, Type.Frame) )
    //             // {
    //             //     textAlignClass = 'textAlignLeft';
    //         // }
    //         else
    //         {
    //             textAlignClass = 'textAlignCenter';
    //         }
    //     }
    //
    //     return (
    //         <div className={cx('tableCellValue', textAlignClass, {isInvalid, isNested})}>
    //             {value}
    //         </div>
    //     );
    // }

    private _renderBgColor():JSX.Element
    {
        const cellSettings = this._settings;

        const {
            isMenuOpened,
            tmpValue
        } = rootState.ui.visualDesigner.workbench.cellSettingsBar;

        let bgColor;

        if( isMenuOpened == CellSettingsBarMenu.BgColor && this._cellInSelectedRange && tmpValue !== null )
            bgColor = tmpValue;
        else if( cellSettings )
            bgColor = cellSettings.bgColor;

        if( !bgColor && !this._isErrorCell )
        {
            return null;
        }

        let bgStyle:React.CSSProperties = {};

        if( bgColor )
        {
            bgStyle.backgroundColor = bgColor;
        }

        return (
            <div
                className={cx('cellBgColor', {
                    isInvalid: this._isErrorCell
                })}
                style={bgStyle}
            />
        );
    }

    /**
     *       0 1 2  -2-1 0    ...    0 1 2  -2-1 0
     *                        ...
     *         *       |      ...      *       |
     *  0      *       |      ...      - - - - |
     *  1  * * - - - - * *    ...    * *       * *
     *  2      |       *      ...      - - - - *
     *         |       *      ...      |       *
     * -2      |       *      ...      |       *
     * -1  - - * * * * - -    ...    - * * * * - -
     *  0      *       |      ...      *       |
     *         *       |      ...      *       |
     */
    private _renderBorder():JSX.Element
    {
        const isGridlinesVisible = this._isGridlinesVisible;

        let borderTop;
        let borderLeft;

        const cellSettings = this._settings;
        if( cellSettings )
        {
            borderTop = cellSettings.borderTop;
            borderLeft = cellSettings.borderLeft;
        }

        // const isBackground   = false;
        // const bgColor = '#' + ('000000' + Math.floor((Math.random() * 0xffffff)).toString(16)).slice(-6);

        // const gridColor = 'rgba(0,0,0,.15)';

        let cellBorderTop = MeshCellBorder.fromBorder(borderTop, isGridlinesVisible);
        let cellBorderLeft = MeshCellBorder.fromBorder(borderLeft, isGridlinesVisible);

        if( cellBorderTop.type == MeshCellBorderType.Grid )
        {
            const topSettings = this._settingsByOffset(0, -1);
            if( topSettings && topSettings.borderBottom && topSettings.borderBottom.type !== MeshCellBorderType.Grid )
            {
                cellBorderTop = MeshCellBorder.fromBorder(topSettings.borderBottom, isGridlinesVisible);
            }
        }

        if( cellBorderLeft.type == MeshCellBorderType.Grid )
        {
            const leftSettings = this._settingsByOffset(-1, 0);
            if( leftSettings && leftSettings.borderRight && leftSettings.borderRight.type !== MeshCellBorderType.Grid )
            {
                cellBorderLeft = MeshCellBorder.fromBorder(leftSettings.borderRight, isGridlinesVisible);
            }
        }

        // let style:any = {}; //this._style;
        // let svgStyleBg:any = {}; //this._style;

        // if (isBackground) {
        //     style.background = isBackground ? bgColor : 'none';
        //     // svgStyleBg.fill = this._isInvalid ? '#F5A422'
        //     //                 : isBackground ? bgColor : 'none';
        // }

        const svgWidth = this._width + 3;
        const svgHeight = this._height + 3;

        const borderLeftY1Pos = cellBorderLeft.type === MeshCellBorderType.Grid ? 2 : 1;
        const borderLeftY2Pos = cellBorderLeft.type === MeshCellBorderType.Grid ? 2 : 1;

        // const borderTopX1Pos = cellBorderTop.type === MeshCellBorderType.Grid ? 2 : 1;
        // let borderTopX1Pos = cellBorderTop.type === MeshCellBorderType.Grid && cellBorderLeft.type !== MeshCellBorderType.Grid ? 2 : 1;
        let borderTopX1Pos = 1;

        if( cellBorderTop.type === MeshCellBorderType.Grid )
        {
            borderTopX1Pos = cellBorderLeft.type === MeshCellBorderType.Grid
                ? 1
                : cellBorderLeft.type === MeshCellBorderType.Thin
                    ? 2
                    : 3; // double
        }
        // left black border over all !!!
        else if( /*cellBorderTop.type !== MeshCellBorderType.Grid && */
            cellBorderLeft.type !== MeshCellBorderType.Grid &&
            cellBorderLeft.color === '#000' &&
            cellBorderTop.color !== '#000' )
        {
            borderTopX1Pos += cellBorderLeft.type === MeshCellBorderType.Thin ? 1 : 2;
        }

        const borderTopX2Pos = cellBorderTop.type === MeshCellBorderType.Grid ? 2 : 1;

        return (
            <svg
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                className={cx('cellBorders')}
            >
                {/*<line x1="0" y1="0" x2="100%" y2="0" stroke="" />*/}
                {/*<line x1="0" y1="0" x2="100%" y2="0" stroke="" />*/}

                {/*{isBackground && (*/}
                {/*<rect x="0" y="0"*/}
                {/*width={svgWidth} height={svgHeight}*/}
                {/*style={svgStyleBg}*/}
                {/*/>*/}
                {/*)}*/}

                {cellBorderLeft.type !== MeshCellBorderType.Double &&
                (
                    <line x1={1.5} y1={borderLeftY1Pos}
                          x2={1.5} y2={svgHeight - borderLeftY2Pos}
                          stroke={cellBorderLeft.color}
                    />
                )}

                {cellBorderLeft.type === MeshCellBorderType.Double &&
                (
                    <line x1={0.5} y1="1"
                          x2={0.5} y2={svgHeight - 1}
                          stroke={cellBorderLeft.color}
                    />
                )}

                {cellBorderLeft.type === MeshCellBorderType.Double &&
                (
                    <line x1={2.5} y1="1"
                          x2={2.5} y2={svgHeight - 1}
                          stroke={cellBorderLeft.color}
                    />
                )}

                {/* ---- */}

                {cellBorderTop.type !== MeshCellBorderType.Double &&
                (
                    <line y1={1.5} x1={borderTopX1Pos}
                          y2={1.5} x2={svgWidth - borderTopX2Pos}
                          stroke={cellBorderTop.color}
                    />
                )}

                {cellBorderTop.type === MeshCellBorderType.Double &&
                (
                    <line y1={0.5} x1={borderTopX1Pos}
                          y2={0.5} x2={svgWidth - 1}
                          stroke={cellBorderTop.color}
                    />
                )}

                {cellBorderTop.type === MeshCellBorderType.Double &&
                (
                    <line y1={2.5} x1={borderTopX1Pos}
                          y2={2.5} x2={svgWidth - 1}
                          stroke={cellBorderTop.color}
                    />
                )}

            </svg>
        );
    }

    private _renderCellActionsLeft()
    {
        const {sheetCtrl} = this.props.ctx;

        if( this._slot && this._isLazyCell )
        {
            const status = sheetCtrl.getSlotStatus(this._slot.sheet.id, this._slot.location);
            const readyToEval = status && status.isEqualTo(SlotStatus.ReadyToEval);

            if( this._isLiveCell )
            {
                const showStopIcon = this._isLiveCellRunning || this._isLiveCellStopping;

                if( showStopIcon )
                {
                    return (
                        <div
                            className={cx('evalLiveCell', {isRunning: true})}
                            onMouseDown={this._handleStopLiveCellMouseDown}
                        >
                            <ImgIcon icon={ImgIcons.stopWhite} className={cx('imgIcon')} />
                        </div>
                    );
                }
                else
                {
                    return (
                        <div
                            className={cx('evalLiveCell', {readyToEval})}
                            onMouseDown={this._handleEvalLiveCellMouseDown}
                        >
                            <ImgIcon icon={ImgIcons.playArrowWhite} className={cx('imgIcon')} />
                        </div>
                    );
                }
            }

            return (
                <div
                    className={cx('evalLazyCell', {readyToEval})}
                    onMouseDown={this._handleEvalLazyCellMouseDown}
                >
                    <ImgIcon icon={ImgIcons.playArrowWhite} className={cx('imgIcon')} />
                </div>
            );
        }

        return null;
    }

    private _getFlowSimulation():FlowSimulation
    {
        const {flowSimulation} = controllers.ui;

        return flowSimulation.getForSlot(this._slot.id);
    }

    private _renderCellActionsRight()
    {
        if(
            ((this._slot && this._isLazyCell) || (this._isFrameCell) || this._isErrorCell) &&
            this._isActive &&
            !this._isEnteredFrameCell
        )
        {
            if( this._isLiveCell && (this._isLiveCellRunning || this._isLiveCellStopping) )
                return null;

            if( Type.isInstanceOf(this._slot.formula, Type.Frame) )
                return null;

            return (
                <button
                    className={cx('editCell')}
                    onMouseDown={this._handleEditCellMouseDown}
                >
                    <ImgIcon icon={ImgIcons.editPencilWhite} className={cx('imgIcon')} />
                </button>
            );
        }

        return null;
    }

    private _handleEvalLazyCellMouseDown = (event:React.MouseEvent<HTMLElement>):void =>
    {
        const {sheetCtrl} = this.props.ctx;

        if( this._slot && this._isLazyCell && this._isActive )
        {
            // handle LEFT button
            if( event.button !== 0 ) return;

            event.stopPropagation();

            if( this._isLoading ) return;

            const {meshSelection} = controllers.ui.spreadsheets;
            meshSelection.checkActiveCellCol(this.props.ctx, this._slot.location.colIndex);
            meshSelection.checkActiveCellRow(this.props.ctx, this._slot.location.rowIndex);

            sheetCtrl.reEvaluateLazySlot(this._slot.sheet.id, this._slot.location);
        }
    };

    private _handleEvalLiveCellMouseDown = (event:React.MouseEvent<HTMLElement>):void =>
    {
        const {sheetCtrl} = this.props.ctx;

        if( this._slot && this._isLiveCell && this._isActive )
        {
            // handle LEFT button
            if( event.button !== 0 ) return;

            event.stopPropagation();

            if( this._isLoading ) return;

            const {meshSelection} = controllers.ui.spreadsheets;
            meshSelection.checkActiveCellCol(this.props.ctx, this._slot.location.colIndex);
            meshSelection.checkActiveCellRow(this.props.ctx, this._slot.location.rowIndex);

            if( this._isEnteredFrameCell )
            {
                this._exitNestedView();
            }

            sheetCtrl.reEvaluateLazySlot(this._slot.sheet.id, this._slot.location);
        }
    };

    private _exitNestedView()
    {
        const {sheetId} = this.props.ctx;

        const {main, frameDetails, tabs} = controllers.ui.spreadsheets;

        const sheet:AnySheet = main.findAnySheetById(sheetId, true);
        const activeSheet = tabs.getActiveSheet(sheet.workbook.id);

        frameDetails.exitNestedView(sheet.workbook.id, activeSheet.id, sheetId);
    }

    private _handleStopLiveCellMouseDown = (event:React.MouseEvent<HTMLElement>):void =>
    {
        const {sheetCtrl} = this.props.ctx;

        if( this._slot && this._isLiveCell && this._isActive )
        {
            // handle LEFT button
            if( event.button !== 0 ) return;

            event.stopPropagation();

            if( this._isLoading ) return;

            const {meshSelection} = controllers.ui.spreadsheets;
            meshSelection.checkActiveCellCol(this.props.ctx, this._slot.location.colIndex);
            meshSelection.checkActiveCellRow(this.props.ctx, this._slot.location.rowIndex);

            sheetCtrl.reEvaluateLazySlot(this._slot.sheet.id, this._slot.location);
        }
    };

    private _handleEditCellMouseDown = (event:React.MouseEvent<HTMLElement>):void =>
    {
        if( this._isActive )
        {
            // handle LEFT button
            if( event.button !== 0 ) return;

            event.stopPropagation();

            const {ctx} = this.props;
            const {mesh} = controllers.ui.spreadsheets;
            mesh.startEditingCell(ctx);
        }
    };

    private _handleFrameCellMouseDown = (event:React.MouseEvent<HTMLElement>) =>
    {
        if( (this._isFrameCell || this._isErrorCell) && this._isActive && !this._isEnteredFrameCell )
        {
            event.preventDefault();
            event.stopPropagation();

            if( this._isLiveCell && this._isLiveCellRunning )
                return;

            // handle only LEFT mouse button
            if( event.button !== 0 )
                return;

            const {ctx} = this.props;
            const {sheetId} = ctx;
            const {main, tabs, frameDetails} = controllers.ui.spreadsheets;

            const sheet:AnySheet = main.findAnySheetById(sheetId, true);
            const activeSheet = tabs.getActiveSheet(sheet.workbook.id);

            // TODO: remove after tests on branch
            (window as any).__enterNestedViewWithAltKey__ = event.altKey;

            frameDetails.enterNestedView(
                sheet.workbook.id,
                activeSheet.id,
                sheetId,
                this._slot.location
            );
        }
    };

    private get _isGridlinesVisible():boolean
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        return meshState.isGridlinesVisible;
    }

    private get _settings():MeshCellSettings | null
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetSettingsCtrl, sheetId} = ctx;

        return sheetSettingsCtrl.getCellSettings(sheetId, new MeshLocation(colIndex, rowIndex).asKey());
    }

    private get _note():MeshCellNote | null
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetSettingsCtrl, sheetId} = ctx;

        return sheetSettingsCtrl.getCellNote(sheetId, new MeshLocation(colIndex, rowIndex).asKey());
    }

    private get _format():IMeshCellValueFormat | null
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetSettingsCtrl, sheetId} = ctx;

        return sheetSettingsCtrl.getCellFormat(sheetId, new MeshLocation(colIndex, rowIndex).asKey());
    }

    private _settingsByOffset(deltaX:number, deltaY:number):MeshCellSettings | null
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetSettingsCtrl, sheetId} = ctx;

        const meshLocation = new MeshLocation(colIndex + deltaX, rowIndex + deltaY);

        return sheetSettingsCtrl.getCellSettings(sheetId, meshLocation.asKey());
    }

    @computed
    private get _cellInSelectedRange():boolean
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {from, to} = meshState.details.selectedRange;

        // col
        if( from.rowIndex === null )
            return colIndex >= from.colIndex && colIndex <= to.colIndex;

        // row
        if( from.colIndex === null )
            return rowIndex >= from.rowIndex && rowIndex <= to.rowIndex;

        return colIndex >= from.colIndex && colIndex <= to.colIndex &&
            rowIndex >= from.rowIndex && rowIndex <= to.rowIndex;
    }

    @computed
    private get _slot():AnySlot
    {
        const {ctx, colIndex, rowIndex} = this.props;
        const {sheetId, sheetCtrl} = ctx;

        const slotLocation:SlotLocation = new SlotLocation(colIndex, rowIndex);

        return sheetCtrl.findSlot(sheetId, slotLocation);
    }

    /*
		@computed
		private get _valueAlign():CellValueAlign
		{
			if( this._slot && this._slot.value != null )
			{
				const type:Type = this._slot.value.getType();

				if(
					type.isEqualTo(Type.Integer) ||
					type.isEqualTo(Type.Number) ||
					type.isEqualTo(Type.Currency)
				)
				{
					return CellValueAlign.Right;
				}
			}

			return CellValueAlign.Left;
		}
	*/

    @computed
    private get _isErrorCell():boolean
    {
        if( this._slot && this._slot.value != null )
        {
            const type:Type = this._slot.value.getType();

            if( type.isEqualTo(Type.Error) )
                return true;
        }

        return false;
    }

    @computed
    private get _isActive():boolean
    {
        const {mesh} = controllers.ui.spreadsheets;
        const {ctx} = this.props;

        if( this._slot )
        {
            const meshState = mesh.getState(ctx);
            const {activeCell} = meshState.details;

            if( activeCell )
            {
                return activeCell.toSlotLocation().isEqualTo(this._slot.location);
            }

            return false;
        }

        return false;
    }

    @computed
    private get _isMouseOver():boolean
    {
        const {ctx} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {cellAtCursor} = meshState.details;

        if( this._slot && cellAtCursor )
        {
            return cellAtCursor.toSlotLocation().isEqualTo(this._slot.location);
        }

        return false;
    }

    @computed
    private get _isLoading():boolean
    {
        const {sheetCtrl} = this.props.ctx;

        return this._slot && sheetCtrl.isSlotLoading(this._slot.sheet.id, this._slot.location);
    }

    @computed
    private get _isFrameCell():boolean
    {
        return this._slot && this._slot.containsFrame;
    }

    @computed
    private get _isEnteredFrameCell():boolean
    {
        const {frameDetails} = controllers.ui.spreadsheets;

        if( this._slot )
        {
            const {sheet, location} = this._slot;
            const enteredFrameCell:MeshLocation = frameDetails.getEnteredFrameCell(sheet.id);

            if( enteredFrameCell )
                return enteredFrameCell.toSlotLocation().isEqualTo(location);
        }

        return false;
    }

    @computed
    private get _isLazyCell():boolean
    {
        return this._slot && this._slot.isLazy;
    }

    @computed
    private get _isLiveCell():boolean
    {
        return this._slot && this._slot.isLive;
    }

    @computed
    private get _isLiveCellRunning():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isRunning(simulation.id);
    }

    @computed
    private get _isLiveCellStopping():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isStopping(simulation.id);
    }

    @computed
    private get _isLiveCellStarting():boolean
    {
        const {flowSimulation} = controllers.ui;

        const simulation = this._getFlowSimulation();

        return simulation && flowSimulation.isStarting(simulation.id);
    }

    @computed
    private get _isCloudSheet():boolean
    {
        const {ctx} = this.props;
        const {main} = controllers.ui.spreadsheets;

        const sheet:AnySheet = main.findAnySheetById(ctx.sheetId, true);

        return sheet && sheet.isCloud;
    }

    @computed
    private get _width():number
    {
        const {ctx, colIndex} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {colWidths, details: {resizingColIndex, resizingColWidth}} = meshState;

        return resizingColIndex === colIndex && resizingColWidth != null
            ? resizingColWidth
            : colWidths[colIndex];
    }

    @computed
    private get _height():number
    {
        const {ctx, rowIndex} = this.props;
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {rowHeights, details: {resizingRowIndex, resizingRowHeight}} = meshState;

        return resizingRowIndex === rowIndex && resizingRowHeight != null
            ? resizingRowHeight
            : rowHeights[rowIndex];
    }

    @computed
    private get _style():React.CSSProperties
    {
        return {
            width: `${this._width}px`
        };
    }

    // private get _valueStyle():React.CSSProperties
    // {
    //     const cellSettings = this._settings;
    //     const cellFont = (cellSettings) ? cellSettings.font : MeshCellFont.default();
    //
    //     return {
    //         ...cellFont.toCSSValue()
    //     };
    // }

    /*
		private _maskValue(value):string
		{
			return value.replace(/./g, '*');
		}
	*/
}
